import type { FocusCaptureSource } from './NoteFocusCapture.ts';

/*
What Get All Notes needs to know about a Prompt visit page, apart from its
values: which note is open, its sections, and the header. Everything here only
reads. The values come from PromptInject's own extractors (its
noteFocusCapture), so they are keyed exactly as its getQuestions keys them.

A section is one block of the visit document, `<div id="Pain" class="full-width
q-pl-md">`, whose id is also its entry in the anchor rail. The Treat & Flow tab
has no sections; its flowsheet table is one section.
*/

export const FLOWSHEET = 'Flowsheet';
const SECTION_SELECTOR = 'div.full-width.q-pl-md[id]';

export const promptNotePage: Omit<FocusCaptureSource, 'readSection'> = {
  getNoteKey,
  sectionOf,
  listSections,
  getPatientInfo,
  getPageInfo,
  getNoteStamp,
  isFinalization: (event) =>
    event.type === 'click' &&
    event.target instanceof Element &&
    isFinalizationTarget(event.target),
  // Quasar shows a toggle's choice through classes (bg-p-blue*).
  observedAttributes: ['class', 'aria-checked', 'aria-pressed', 'aria-selected']
};

function isFinalizationTarget(target: Element) {
  const button = target.closest(
    '#checkoutModalPage[data-is-open="true"] [data-testid="button-checkoutSendToReview"]'
  );
  return Boolean(
    button &&
    !button.closest('[disabled], [aria-disabled="true"], [aria-busy="true"], .disabled, .q-btn--loading, [hidden], [inert]')
  );
}

/* The visit page is /patients/{patientId}?visitId={visitId}&patientId=... */
function getNoteKey() {
  const patientId = window.location.pathname.match(/^\/patients\/([^/]+)/)?.[1];
  const visitId = new URLSearchParams(window.location.search).get('visitId');
  return patientId && visitId ? `patient:${patientId}|visit:${visitId}` : null;
}

function sectionOf(node: Node) {
  const el = node instanceof Element ? node : node.parentElement;
  if (!el) return null;
  const section = el.closest(SECTION_SELECTOR);
  if (section) return section.id;
  return el.closest('table')?.querySelector('.omni-type-s') ? FLOWSHEET : null;
}

/* Sections in the anchor rail that are on the page, plus a shown flowsheet. */
function listSections() {
  const sections = Array.from(document.querySelectorAll('[anchor-key]'))
    .map((a) => a.getAttribute('anchor-key') || '')
    .filter((id) => document.getElementById(id)?.matches(SECTION_SELECTOR));
  const flowsheet = Array.from(document.querySelectorAll('table')).some((t) =>
    t.querySelector('.omni-type-s')
  );
  if (flowsheet) sections.push(FLOWSHEET);
  return sections;
}

/* The patient header and the visit button, which name the note on screen. */
function getNoteStamp() {
  const caption = Array.from(document.querySelectorAll('.p-text-caption')).find(
    (el) => el.textContent?.trim() === 'Patient'
  );
  return [
    text(caption?.parentElement),
    text(document.querySelector('button.q-btn-dropdown .q-btn__content'))
  ].join('|');
}

/* Same read as the injectors' getPageInfo: the visit button's first line. */
function getPageInfo() {
  const dropdown = document.querySelector(
    'button.q-btn-dropdown .q-btn__content .column > div'
  );
  const parts = (dropdown?.textContent || '').split(' - ');
  const visitType = parts.length > 1 ? parts[parts.length - 1].trim() : '';
  return {
    URL: window.location.origin,
    'Note Type': visitType || 'Prompt Note'
  };
}

/*
The header half of the injectors' getPatientInfo. The rest of it (DOB and
metadata) comes from the ledger menu and intake dialog, which it has to open.
*/
function getPatientInfo() {
  const caption = Array.from(document.querySelectorAll('.p-text-caption')).find(
    (el) => el.textContent?.trim() === 'Patient'
  );
  const nameEl = caption?.parentElement?.children[1];
  if (!nameEl) return {};
  const fullName = nameEl.textContent?.trim() || '';
  const match = fullName.match(/^(\w+)\s*(?:"(\w+)")?\s+(\w+)$/);
  return {
    'EMR ID': text(nameEl.nextElementSibling).replace('Acct#:', '').trim(),
    'First Name': match
      ? match[2]
        ? `${match[1]} "${match[2]}"`
        : match[1]
      : fullName,
    'Last Name': match ? match[3] : ''
  };
}

function text(el: Element | null | undefined) {
  return (el?.textContent || '').replace(/\s+/g, ' ').trim();
}
